/**
 * JWT payload decoding utilities.
 *
 * These functions decode the payload section of a JWT without verifying
 * the signature. Signature verification happens server-side (Gateway + IDAAS).
 * The client only reads claims for display and permission checking.
 */

import { AUTHORIZATION_SCOPE_KIND } from "@zero/idaas-api";
import { parseTenantId } from "./tenant-id";
import type { AccessTokenPayload, AuthScope, IdTokenPayload, ScopedAuthorizationGrant, UserInfo } from "./types";

const MAX_IDENTIFIER_BYTES = 256;
const MAX_OIDC_SCOPE_BYTES = 4096;
const MAX_EMAIL_BYTES = 320;
const MAX_NAME_BYTES = 256;
const MAX_PICTURE_URL_BYTES = 2048;
const MAX_ROLES = 256;
const MAX_GRANTS = 256;
const MAX_GRANT_PERMISSIONS = 256;
const MAX_JWT_BYTES = 16_384;
const TOKEN_CLOCK_SKEW_SECONDS = 60;

function isBoundedString(value: unknown, maxBytes = MAX_IDENTIFIER_BYTES, allowEmpty = false): value is string {
  return (
    typeof value === "string" && (allowEmpty || value.length > 0) && new TextEncoder().encode(value).length <= maxBytes
  );
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isUnixTimestamp(value: unknown): value is number {
  return isFiniteNumber(value) && Number.isSafeInteger(value) && value >= 0;
}

function isStringList(
  value: unknown,
  maxItems: number,
  maxItemBytes = MAX_IDENTIFIER_BYTES,
): value is readonly string[] {
  return Array.isArray(value) && value.length <= maxItems && value.every((item) => isBoundedString(item, maxItemBytes));
}

function isAccessTokenPayload(value: unknown): value is AccessTokenPayload {
  if (typeof value !== "object" || value === null) return false;
  const payload = value as Record<string, unknown>;
  return (
    payload.token_use === "access" &&
    isBoundedString(payload.sub) &&
    (payload.gty === "client_credentials" ? payload.sid === undefined : isBoundedString(payload.sid)) &&
    isBoundedString(payload.azp) &&
    isBoundedString(payload.zid) &&
    isBoundedString(payload.gty) &&
    isBoundedString(payload.scope, MAX_OIDC_SCOPE_BYTES, true) &&
    isBoundedString(payload.realm_id) &&
    isBoundedString(payload.tenant_id) &&
    isBoundedString(payload.iss, MAX_PICTURE_URL_BYTES) &&
    isBoundedString(payload.aud) &&
    (payload.roles === undefined || isStringList(payload.roles, MAX_ROLES)) &&
    (payload.authorization_grants === undefined || Array.isArray(payload.authorization_grants)) &&
    isUnixTimestamp(payload.exp) &&
    isUnixTimestamp(payload.iat)
  );
}

function isIdTokenPayload(value: unknown): value is IdTokenPayload {
  if (typeof value !== "object" || value === null) return false;
  const payload = value as Record<string, unknown>;
  return (
    payload.token_use === "id" &&
    isBoundedString(payload.sub) &&
    isBoundedString(payload.sid) &&
    isBoundedString(payload.azp) &&
    isBoundedString(payload.gty) &&
    isBoundedString(payload.realm_id) &&
    isBoundedString(payload.tenant_id) &&
    isBoundedString(payload.iss, MAX_PICTURE_URL_BYTES) &&
    isAudience(payload.aud) &&
    (payload.email === undefined || isBoundedString(payload.email, MAX_EMAIL_BYTES, true)) &&
    (payload.name === undefined || isBoundedString(payload.name, MAX_NAME_BYTES, true)) &&
    (payload.picture === undefined || isBoundedString(payload.picture, MAX_PICTURE_URL_BYTES, true)) &&
    isUnixTimestamp(payload.exp) &&
    isUnixTimestamp(payload.iat)
  );
}

function isAudience(value: unknown): value is string | readonly string[] {
  return isBoundedString(value) || (isStringList(value, 16) && value.length > 0);
}

/**
 * Decode the payload of a JWT without signature verification.
 *
 * Returns `null` if the token is malformed or the payload is not valid JSON.
 */
export function decodeJwtPayload<T = Record<string, unknown>>(token: string): T | null {
  try {
    if (new TextEncoder().encode(token).length > MAX_JWT_BYTES) return null;
    const parts = token.split(".");
    if (parts.length !== 3) {
      return null;
    }
    const payload = parts[1];
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    const json = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

/** Decode an access token and return typed claims, or `null`. */
export function decodeAccessTokenPayload(token: string): AccessTokenPayload | null {
  const payload = decodeJwtPayload<unknown>(token);
  return isAccessTokenPayload(payload) ? payload : null;
}

/** Decode an ID token and return typed claims, or `null`. */
export function decodeIdTokenPayload(token: string): IdTokenPayload | null {
  const payload = decodeJwtPayload<unknown>(token);
  return isIdTokenPayload(payload) ? payload : null;
}

/** Validate provider, client, and time bindings before a token can be used. */
export function tokenClaimsMatchProvider(
  accessPayload: AccessTokenPayload,
  idPayload: IdTokenPayload | null,
  expectedIssuer: string,
  expectedClientId: string,
  expectedResource: string,
  nowMs = Date.now(),
  allowExpired = false,
): boolean {
  if (
    accessPayload.iss !== expectedIssuer ||
    accessPayload.aud !== expectedResource ||
    accessPayload.azp !== expectedClientId ||
    !tokenTimelineIsValid(accessPayload, nowMs, allowExpired)
  ) {
    return false;
  }
  if (!idPayload) return true;
  return (
    idPayload.iss === expectedIssuer &&
    audienceContains(idPayload.aud, expectedClientId) &&
    idPayload.azp === expectedClientId &&
    tokenTimelineIsValid(idPayload, nowMs, allowExpired) &&
    tokenPairHasSameBinding(idPayload, accessPayload)
  );
}

/** Compare all session/authorization claims that must survive refresh rotation. */
export function accessTokensHaveSameSessionBinding(previous: AccessTokenPayload, next: AccessTokenPayload): boolean {
  return (
    previous.iss === next.iss &&
    previous.aud === next.aud &&
    previous.azp === next.azp &&
    previous.sub === next.sub &&
    previous.sid === next.sid &&
    previous.zid === next.zid &&
    previous.gty === next.gty &&
    previous.realm_id === next.realm_id &&
    previous.tenant_id === next.tenant_id
  );
}

function tokenTimelineIsValid(payload: { exp: number; iat: number }, nowMs: number, allowExpired: boolean): boolean {
  const nowSeconds = Math.floor(nowMs / 1000);
  return (
    (allowExpired || payload.exp > nowSeconds) &&
    payload.exp > payload.iat &&
    payload.iat <= nowSeconds + TOKEN_CLOCK_SKEW_SECONDS
  );
}

function audienceContains(audience: string | readonly string[], expected: string): boolean {
  return typeof audience === "string" ? audience === expected : audience.includes(expected);
}

/**
 * Extract the `exp` claim from a JWT as a Unix timestamp in milliseconds.
 * Returns `null` if the token is malformed or has no `exp` claim.
 */
export function getTokenExpiry(token: string): number | null {
  const payload = decodeJwtPayload<{ exp?: number }>(token);
  if (!isUnixTimestamp(payload?.exp)) {
    return null;
  }
  const expiry = payload.exp * 1000;
  return Number.isSafeInteger(expiry) ? expiry : null;
}

/**
 * Build a `UserInfo` from a pair of decoded JWT payloads.
 *
 * Merges identity claims from the ID token with authorization claims
 * (roles, assignment-scoped permissions, scope) from the access token.
 */
export function buildUserInfo(
  idPayload: IdTokenPayload | null,
  accessPayload: AccessTokenPayload | null,
): UserInfo | null {
  if (idPayload && accessPayload && !tokenPairHasSameBinding(idPayload, accessPayload)) {
    return null;
  }
  const source = idPayload ?? accessPayload;
  if (!source?.sub || !isBoundedString(source.sub)) {
    return null;
  }
  if (!accessPayload || accessPayload.gty === "client_credentials" || !accessPayload.sid) {
    return null;
  }
  const idAny = idPayload as Record<string, unknown> | null;
  const realmId =
    nullableBoundedString(accessPayload?.realm_id, MAX_IDENTIFIER_BYTES) ??
    nullableBoundedString(idAny?.realm_id, MAX_IDENTIFIER_BYTES);
  if (!realmId) return null;

  const tenantId = parseTenantId(
    nullableBoundedString(accessPayload?.tenant_id, MAX_IDENTIFIER_BYTES) ??
      nullableBoundedString(idAny?.tenant_id, MAX_IDENTIFIER_BYTES),
  );
  if (!tenantId) return null;
  const authorizationGrants = normalizeAuthorizationGrants(accessPayload?.authorization_grants);
  if (authorizationGrants === null) return null;
  return {
    sub: source.sub,
    email: nullableBoundedString(idAny?.email, MAX_EMAIL_BYTES),
    name: nullableBoundedString(idAny?.name, MAX_NAME_BYTES),
    picture: nullableBoundedString(idAny?.picture, MAX_PICTURE_URL_BYTES),
    realmId,
    tenantId,
    roles: normalizeStringList(accessPayload?.roles, MAX_ROLES),
    authorizationGrants,
    permissions: flattenPermissions(authorizationGrants),
    scope: extractScopeFromGrants(authorizationGrants),
  };
}

function tokenPairHasSameBinding(idPayload: IdTokenPayload, accessPayload: AccessTokenPayload): boolean {
  return (
    idPayload.iss === accessPayload.iss &&
    audienceContains(idPayload.aud, accessPayload.azp) &&
    idPayload.sub === accessPayload.sub &&
    idPayload.sid === accessPayload.sid &&
    idPayload.azp === accessPayload.azp &&
    idPayload.gty === accessPayload.gty &&
    idPayload.realm_id === accessPayload.realm_id &&
    idPayload.tenant_id === accessPayload.tenant_id
  );
}

function normalizeStringList(value: unknown, max: number): readonly string[] {
  return isStringList(value, max) ? [...value] : [];
}

function normalizeAuthorizationGrants(value: unknown): readonly ScopedAuthorizationGrant[] | null {
  if (value === undefined) return [];
  if (!Array.isArray(value) || value.length > MAX_GRANTS) return null;

  const normalized: ScopedAuthorizationGrant[] = [];
  for (const candidate of value) {
    if (typeof candidate !== "object" || candidate === null) return null;
    const grant = candidate as Record<string, unknown>;
    const role = grant.role;
    const permissions = grant.permissions;
    const targetId = grant.scope_target_id;
    const scopeKind = grant.scope_kind;
    if (!isBoundedString(role) || !isStringList(permissions, MAX_GRANT_PERMISSIONS) || !isBoundedString(targetId)) {
      return null;
    }

    const scope = scopeFromKind(scopeKind, targetId);
    if (!scope) return null;
    normalized.push({ role, permissions: [...permissions], scope });
  }
  return normalized;
}

function normalizeScopedAuthorizationGrants(value: unknown): readonly ScopedAuthorizationGrant[] | null {
  if (!Array.isArray(value) || value.length > MAX_GRANTS) return null;

  const normalized: ScopedAuthorizationGrant[] = [];
  for (const candidate of value) {
    if (typeof candidate !== "object" || candidate === null) return null;
    const grant = candidate as Record<string, unknown>;
    const scope = grant.scope;
    if (
      !isBoundedString(grant.role) ||
      !isStringList(grant.permissions, MAX_GRANT_PERMISSIONS) ||
      typeof scope !== "object" ||
      scope === null
    ) {
      return null;
    }
    const scopeValue = scope as Record<string, unknown>;
    if (!isBoundedString(scopeValue.targetId)) return null;
    if (scopeValue.kind !== "platform" && scopeValue.kind !== "tenant" && scopeValue.kind !== "realm") return null;
    normalized.push({
      role: grant.role,
      permissions: [...grant.permissions],
      scope: { kind: scopeValue.kind, targetId: scopeValue.targetId },
    });
  }
  return normalized;
}

function flattenPermissions(grants: readonly ScopedAuthorizationGrant[]): readonly string[] {
  const permissions = new Set<string>();
  for (const grant of grants ?? []) {
    for (const permission of grant.permissions) {
      if (permission.length > 0) {
        permissions.add(permission);
      }
    }
  }
  return [...permissions];
}

function scopeFromKind(scopeKind: unknown, targetId: string): AuthScope | null {
  if (scopeKind === AUTHORIZATION_SCOPE_KIND.PLATFORM) {
    return { kind: "platform", targetId };
  }
  if (scopeKind === AUTHORIZATION_SCOPE_KIND.TENANT) {
    return { kind: "tenant", targetId };
  }
  if (scopeKind === AUTHORIZATION_SCOPE_KIND.REALM) {
    return { kind: "realm", targetId };
  }
  return null;
}

function extractScopeFromGrants(grants: readonly ScopedAuthorizationGrant[]): AuthScope | null {
  const rank: readonly AuthScope["kind"][] = ["platform", "tenant", "realm"];
  for (const kind of rank) {
    const targets = new Set(
      grants.filter((candidate) => candidate.scope.kind === kind).map((candidate) => candidate.scope.targetId),
    );
    if (targets.size === 0) continue;
    if (targets.size !== 1) return null;
    const targetId = targets.values().next().value;
    if (typeof targetId !== "string") return null;
    return { kind, targetId };
  }
  return null;
}

function nullableBoundedString(value: unknown, maxBytes: number): string | null {
  return isBoundedString(value, maxBytes, true) ? value : null;
}

/** Normalize an optional profile claim while preserving a trusted fallback. */
export function normalizeProfileValue(value: unknown, fallback: string | null, maxBytes: number): string | null {
  return value === undefined ? fallback : nullableBoundedString(value, maxBytes);
}

/** Validate a `UserInfo` snapshot read from untrusted browser storage. */
export function parseStoredUserInfo(value: unknown): UserInfo | null {
  if (typeof value !== "object" || value === null) return null;
  const stored = value as Record<string, unknown>;
  if (!isBoundedString(stored.sub) || !isBoundedString(stored.realmId)) return null;
  const tenantId = parseTenantId(stored.tenantId);
  if (!tenantId) return null;

  const authorizationGrants = normalizeScopedAuthorizationGrants(stored.authorizationGrants);
  if (authorizationGrants === null) return null;
  return {
    sub: stored.sub,
    email: nullableBoundedString(stored.email, MAX_EMAIL_BYTES),
    name: nullableBoundedString(stored.name, MAX_NAME_BYTES),
    picture: nullableBoundedString(stored.picture, MAX_PICTURE_URL_BYTES),
    realmId: stored.realmId,
    tenantId,
    roles: normalizeStringList(stored.roles, MAX_ROLES),
    authorizationGrants,
    permissions: flattenPermissions(authorizationGrants),
    scope: extractScopeFromGrants(authorizationGrants),
  };
}

export type StoredUserProfile = Pick<UserInfo, "sub" | "realmId" | "tenantId" | "email" | "name" | "picture">;

/** Validate the profile-only portion of an atomic session snapshot. */
export function parseStoredUserProfile(value: unknown): StoredUserProfile | null {
  if (typeof value !== "object" || value === null) return null;
  const stored = value as Record<string, unknown>;
  if (!isBoundedString(stored.sub) || !isBoundedString(stored.realmId)) return null;
  const tenantId = parseTenantId(stored.tenantId);
  if (!tenantId) return null;
  return {
    sub: stored.sub,
    realmId: stored.realmId,
    tenantId,
    email: nullableBoundedString(stored.email, MAX_EMAIL_BYTES),
    name: nullableBoundedString(stored.name, MAX_NAME_BYTES),
    picture: nullableBoundedString(stored.picture, MAX_PICTURE_URL_BYTES),
  };
}

/**
 * Derive the widest valid authorization scope from assignment-scoped grants.
 *
 * Returning `null` when no single unambiguous grant target exists is the
 * fail-closed behavior required by `<ScopeGuard>`.
 */
export function extractScope(accessPayload: AccessTokenPayload | null): AuthScope | null {
  const grants = normalizeAuthorizationGrants(accessPayload?.authorization_grants);
  return grants === null ? null : extractScopeFromGrants(grants);
}
